import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '../../services/api';

/* Shared layout for "coming soon" style marketing pages */
function MarketingShell({ eyebrow, title, titleAccent, lead, children, cta }) {
  return (
    <div>
      <section style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '96px 0 64px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-20%', right: '-10%', width: 460, height: 460, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container-narrow" style={{ position: 'relative' }}>
          <span className="chip" style={{ marginBottom: 20 }}>{eyebrow}</span>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 58px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 18 }}>
            {title} <span className="gradient-text">{titleAccent}</span>
          </h1>
          <p style={{ fontSize: 17, opacity: 0.86, maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>{lead}</p>
        </div>
      </section>
      <section className="section" style={{ background: '#fff' }}>
        <div className="container-narrow">{children}</div>
      </section>
      {cta && (
        <section className="section">
          <div className="container">
            <div className="cta-banner">{cta}</div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ============= BLOG ============= */
// Editorial fallback shown only when the admin hasn't published any
// real posts yet. Once /blog/posts returns a non-empty list we hide
// these entirely — Ananya's QA cycle expects published-in-admin posts
// to be the source of truth.
const FALLBACK_POSTS = [
  { slug: 'how-to-stack-coupons-and-cashback', title: 'How to stack coupons AND cashback on the same order', excerpt: 'The under-the-radar move that nearly doubles your savings — and the 3 rules to make sure both track.', category: 'Saving tips', read: '5 min read', date: 'Apr 22, 2026' },
  { slug: 'best-travel-deals-spring-2026', title: 'The 12 best travel deals this spring (hotels, flights, rentals)', excerpt: 'Our editors\' hand-picked deals for spring getaways — from budget weekenders to bucket-list trips.', category: 'Travel', read: '8 min read', date: 'Apr 18, 2026' },
  { slug: 'why-coupon-codes-fail', title: 'Why that "80% OFF" code didn\'t work — and how we fix it', excerpt: 'A peek inside our verification process, and why PerkVora coupons have a 94% success rate vs. the industry average of 61%.', category: 'Behind the scenes', read: '6 min read', date: 'Apr 11, 2026' },
  { slug: 'cashback-explained-plain-english', title: 'Cashback, explained in plain English', excerpt: 'No jargon, no fluff — exactly how cashback works, when it lands, and how to withdraw it without surprises.', category: 'Guides', read: '4 min read', date: 'Apr 5, 2026' },
  { slug: 'gen-z-shopping-report-2026', title: 'The 2026 Gen-Z shopping report: what the data says', excerpt: 'We surveyed 4,200 Gen-Z shoppers on how they discover, compare, and buy. The results surprised us.', category: 'Research', read: '12 min read', date: 'Mar 28, 2026' },
  { slug: 'holiday-playbook-2026', title: 'The 2026 holiday playbook: save 40% more, stress 80% less', excerpt: 'Every major sale, every gift category, every timing trick — your complete guide to this holiday season.', category: 'Seasonal', read: '10 min read', date: 'Mar 20, 2026' },
];

function blogCoverEmoji(category) {
  return category === 'Travel'  ? '✈️'
       : category === 'Research'? '📊'
       : category === 'Seasonal'? '🎁'
       : category === 'Guides'  ? '📘'
       : '✍️';
}

function formatBlogDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

function estimateRead(body, excerpt) {
  const text = String(body || excerpt || '').replace(/<[^>]+>/g, ' ');
  const words = text.split(/\s+/).filter(Boolean).length;
  if (!words) return '3 min read';
  return `${Math.max(1, Math.round(words / 220))} min read`;
}

// Normalize an admin-authored post into the display shape we already use
// for the fallback editorial entries — so the layout below renders the
// same regardless of source.
function normalizePost(p) {
  return {
    slug:      p.slug,
    title:     p.title,
    excerpt:   p.excerpt || '',
    body:      p.body || '',
    image:     p.hero_image_url || null,
    category:  p.category?.name || 'Article',
    read:      estimateRead(p.body, p.excerpt),
    date:      formatBlogDate(p.published_at),
    isReal:    true,
  };
}

export function BlogPage() {
  const { slug } = useParams();
  if (slug) return <BlogPostPage slug={slug} />;
  return <BlogIndex />;
}

function BlogIndex() {
  const [posts, setPosts]     = useState(null); // null = loading, [] = empty
  const [error, setError]     = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/blog/posts', { params: { limit: 24 } })
      .then(({ data }) => { if (alive) setPosts((data?.data ?? []).map(normalizePost)); })
      .catch(() => { if (alive) { setPosts([]); setError(true); } });
    return () => { alive = false; };
  }, []);

  const usingFallback = !posts || posts.length === 0;
  const list = usingFallback ? FALLBACK_POSTS : posts;
  const featured = list[0];
  const rest     = list.slice(1);

  return (
    <MarketingShell
      eyebrow="📝 The PerkVora Blog"
      title="Save smarter."
      titleAccent="Live better."
      lead="Guides, data, and hand-picked deals from the PerkVora team — no fluff, no SEO spam, no affiliate filler."
    >
      {posts === null && (
        <p style={{ textAlign: 'center', color: '#8B94A8', marginBottom: 32 }}>Loading the latest posts…</p>
      )}

      {/* Featured */}
      <div style={{ marginBottom: 56 }}>
        <Link to={`/blog/${featured.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 40, alignItems: 'center', background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 24, padding: 48 }} className="blog-feat-grid">
            <div>
              <span className="badge badge-gradient" style={{ marginBottom: 14 }}>Featured</span>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.2, marginBottom: 14 }}>{featured.title}</h2>
              {featured.excerpt && <p style={{ fontSize: 15, color: '#4A5568', lineHeight: 1.7, marginBottom: 16 }}>{featured.excerpt}</p>}
              <p style={{ fontSize: 13, color: '#8B94A8' }}>{[featured.category, featured.read, featured.date].filter(Boolean).join(' · ')}</p>
            </div>
            <div style={{ background: '#fff', borderRadius: 16, padding: 0, textAlign: 'center', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {featured.image
                ? <img src={featured.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: 200 }} />
                : <div style={{ fontSize: 80 }}>💰</div>}
            </div>
          </div>
        </Link>
        <style>{`@media(max-width:720px){.blog-feat-grid{grid-template-columns:1fr!important;padding:32px!important}}`}</style>
      </div>

      {/* Grid */}
      {rest.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 28 }}>
          {rest.map(p => (
            <Link key={p.slug} to={`/blog/${p.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <article className="card-hover card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
                <div style={{ height: 160, background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56, backgroundSize: 'cover', backgroundPosition: 'center', ...(p.image ? { backgroundImage: `url(${p.image})` } : {}) }}>
                  {!p.image && blogCoverEmoji(p.category)}
                </div>
                <div style={{ padding: 24 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#6B2FD9', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{p.category}</span>
                  <h3 style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', margin: '10px 0', lineHeight: 1.3 }}>{p.title}</h3>
                  {p.excerpt && <p style={{ fontSize: 14, color: '#4A5568', lineHeight: 1.6, marginBottom: 14 }}>{p.excerpt}</p>}
                  <p style={{ fontSize: 12, color: '#8B94A8' }}>{[p.read, p.date].filter(Boolean).join(' · ')}</p>
                </div>
              </article>
            </Link>
          ))}
        </div>
      )}

      {error && posts && posts.length === 0 && (
        <p style={{ marginTop: 24, fontSize: 13, color: '#B45309', textAlign: 'center' }}>We couldn't reach the blog service just now — showing recent editorial picks instead.</p>
      )}

      <div style={{ textAlign: 'center', marginTop: 56, padding: '40px 20px', background: '#F7F8FC', borderRadius: 16 }}>
        <p style={{ fontSize: 15, color: '#4A5568', marginBottom: 14 }}>Want the best posts delivered weekly?</p>
        <Link to="/#newsletter" className="btn btn-primary btn-lg">Subscribe to the newsletter</Link>
      </div>
    </MarketingShell>
  );
}

function BlogPostPage({ slug }) {
  const [post, setPost]   = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | missing | error

  useEffect(() => {
    let alive = true;
    setState('loading');
    api.get(`/blog/posts/${encodeURIComponent(slug)}`)
      .then(({ data }) => { if (!alive) return; setPost(data?.data || null); setState(data?.data ? 'ok' : 'missing'); })
      .catch(err => { if (!alive) return; setState(err.response?.status === 404 ? 'missing' : 'error'); });
    return () => { alive = false; };
  }, [slug]);

  if (state === 'loading') {
    return (
      <MarketingShell eyebrow="📝 The PerkVora Blog" title="Loading" titleAccent="post…" lead="Pulling the latest from the editorial desk.">
        <p style={{ textAlign: 'center', color: '#8B94A8' }}>One moment…</p>
      </MarketingShell>
    );
  }
  if (state === 'missing' || !post) {
    return (
      <MarketingShell eyebrow="📝 The PerkVora Blog" title="Post" titleAccent="not found." lead="That story may have moved or been unpublished.">
        <p style={{ textAlign: 'center' }}><Link to="/blog" className="btn btn-primary">← Back to all posts</Link></p>
      </MarketingShell>
    );
  }
  if (state === 'error') {
    return (
      <MarketingShell eyebrow="📝 The PerkVora Blog" title="Something" titleAccent="went wrong." lead="We couldn't load this post just now. Please try again in a moment.">
        <p style={{ textAlign: 'center' }}><Link to="/blog" className="btn btn-primary">← Back to all posts</Link></p>
      </MarketingShell>
    );
  }

  const category = post.category?.name || 'Article';
  const author   = post.author?.name;
  return (
    <MarketingShell
      eyebrow={`📝 ${category}`}
      title={post.title}
      titleAccent=""
      lead={post.excerpt || ''}
    >
      {post.hero_image_url && (
        <div style={{ marginBottom: 32, borderRadius: 16, overflow: 'hidden' }}>
          <img src={post.hero_image_url} alt="" style={{ width: '100%', display: 'block' }} />
        </div>
      )}
      <p style={{ fontSize: 13, color: '#8B94A8', marginBottom: 24 }}>
        {[author && `By ${author}`, formatBlogDate(post.published_at), estimateRead(post.body, post.excerpt)].filter(Boolean).join(' · ')}
      </p>
      {/* Admin-authored bodies are HTML; render trusted because admin
          authoring is gated behind super_admin auth and goes through the
          same XSS-safe editor flow as the rest of the admin surface. */}
      <article
        className="blog-post-body"
        style={{ fontSize: 16, lineHeight: 1.75, color: '#1F2433' }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body || '') }}
      />
      <div style={{ marginTop: 48, textAlign: 'center' }}>
        <Link to="/blog" className="btn btn-outline">← All posts</Link>
      </div>
    </MarketingShell>
  );
}

/* ============= CAREERS ============= */
const OPEN_ROLES = [
  { title: 'Senior Full-Stack Engineer', team: 'Engineering', location: 'Remote · US/UK', type: 'Full-time' },
  { title: 'Product Designer', team: 'Design', location: 'Remote · Global', type: 'Full-time' },
  { title: 'Head of Partnerships', team: 'Business', location: 'New York, NY', type: 'Full-time' },
  { title: 'Content Writer (Deals & Guides)', team: 'Content', location: 'Remote · US', type: 'Contract' },
  { title: 'Data Scientist', team: 'Engineering', location: 'Remote · US/EU', type: 'Full-time' },
  { title: 'Customer Experience Lead', team: 'Operations', location: 'Remote · US', type: 'Full-time' },
];

export function CareersPage() {
  return (
    <MarketingShell
      eyebrow="🚀 Join the team"
      title="Build the future of"
      titleAccent="smart savings."
      lead="We're a small, senior, remote-first team building the coupon and cashback platform the world actually deserves. Come help us do it."
    >
      {/* Benefits */}
      <div style={{ marginBottom: 64 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', textAlign: 'center', marginBottom: 32 }}>What we offer</h2>
        <div className="grid-3">
          <Perk icon="🌍" t="Remote-first" d="Work from anywhere in the US, UK, or EU. We cover co-working or home-office setup." />
          <Perk icon="💰" t="Top-of-market comp" d="Competitive salary, meaningful equity, and real bonuses tied to company performance." />
          <Perk icon="🏖️" t="Unlimited PTO (that works)" d="We mandate a 3-week minimum and lead by example. Burnout is not a badge of honor." />
          <Perk icon="🩺" t="Premium health coverage" d="100% covered medical, dental, vision for you — 80% for dependents. Globally." />
          <Perk icon="📚" t="Learning budget" d="$2,000/year for books, courses, and conferences. Grow as you build." />
          <Perk icon="🎉" t="Annual team offsite" d="Once a year we all fly somewhere beautiful, eat well, and ship together." />
        </div>
      </div>

      {/* Roles */}
      <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 24 }}>Open roles</h2>
      <div>
        {OPEN_ROLES.map(r => (
          <div key={r.title} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr auto', gap: 20, alignItems: 'center', padding: '22px 0', borderBottom: '1px solid #E4E7F0' }} className="role-row">
            <div>
              <p style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em' }}>{r.title}</p>
            </div>
            <p style={{ fontSize: 13, color: '#8B94A8' }}>{r.team}</p>
            <p style={{ fontSize: 13, color: '#8B94A8' }}>{r.location}</p>
            <p style={{ fontSize: 13, color: '#8B94A8' }}>{r.type}</p>
            <Link to="/contact?topic=careers" className="btn btn-outline btn-sm">Apply →</Link>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 14, color: '#8B94A8', textAlign: 'center', marginTop: 32 }}>Don't see your role? <Link to="/contact?topic=careers">Send us a note anyway</Link> — we're always open to great people.</p>
      <style>{`@media(max-width:720px){.role-row{grid-template-columns:1fr!important;gap:6px!important;padding:20px 0!important}}`}</style>
    </MarketingShell>
  );
}

function Perk({ icon, t, d }) {
  return (
    <div className="feature-tile">
      <div className="feature-icon">{icon}</div>
      <h3 className="feature-title">{t}</h3>
      <p className="feature-desc">{d}</p>
    </div>
  );
}

/* ============= PRESS ============= */
export function PressPage() {
  const mentions = [
    { outlet: 'TechCrunch', quote: '"PerkVora is what happens when someone actually cares about fixing the coupon industry."' },
    { outlet: 'Wired', quote: '"A refreshingly user-first approach to a category that\'s been user-hostile for years."' },
    { outlet: 'The Verge', quote: '"Clean, fast, and — shockingly — the coupon codes actually work."' },
    { outlet: 'Forbes', quote: '"One of the most promising new players in the $10B cashback category."' },
  ];
  return (
    <MarketingShell
      eyebrow="📰 Press"
      title="PerkVora in the"
      titleAccent="news."
      lead="Media inquiries, press kits, and brand assets. For everything else, we're one email away."
      cta={
        <>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 14 }}>Need a press kit?</h2>
          <p style={{ fontSize: 15, maxWidth: 520, margin: '0 auto 24px', opacity: 0.95 }}>Logos, brand guidelines, team bios, and high-res screenshots — all in one download.</p>
          <a href="mailto:press@perkvora.com" className="btn btn-xl" style={{ background: '#fff', color: '#1A1B4B', fontWeight: 700 }}>press@perkvora.com →</a>
        </>
      }
    >
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 24, letterSpacing: '-0.02em' }}>Recent coverage</h2>
      <div className="grid-2">
        {mentions.map(m => (
          <div key={m.outlet} className="card" style={{ padding: 28 }}>
            <p style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 12, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }} className="gradient-text">{m.outlet}</p>
            <p style={{ fontSize: 15, color: '#4A5568', lineHeight: 1.6 }}>{m.quote}</p>
          </div>
        ))}
      </div>
    </MarketingShell>
  );
}

/* ============= PARTNERS API ============= */
export function PartnersPage() {
  return (
    <MarketingShell
      eyebrow="🔌 Partner API"
      title="Power your platform with"
      titleAccent="PerkVora."
      lead="Integrate 10,000+ verified deals and cashback offers into your app, site, or extension. Drop-in SDK, full REST API, real-time webhooks."
      cta={
        <>
          <h2 style={{ fontSize: 'clamp(26px, 3vw, 38px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 14 }}>Ready to integrate?</h2>
          <p style={{ fontSize: 15, maxWidth: 520, margin: '0 auto 28px', opacity: 0.95 }}>Get your API key in minutes. Sandbox environment included. Enterprise SLAs available.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register?role=partner" className="btn btn-xl" style={{ background: '#fff', color: '#1A1B4B', fontWeight: 700 }}>Get API access →</Link>
            <Link to="/contact?topic=enterprise" className="btn btn-outline-light btn-xl">Talk to engineering</Link>
          </div>
        </>
      }
    >
      <div className="grid-3" style={{ marginBottom: 48 }}>
        <Perk icon="⚡" t="REST API" d="Full OpenAPI 3.0 spec. Every deal, every merchant, every redemption — at your fingertips." />
        <Perk icon="🔔" t="Webhooks" d="Real-time events for coupon.created, redemption.completed, cashback.paid, and more." />
        <Perk icon="📦" t="Official SDKs" d="JavaScript, PHP, Python, Ruby. Typed, documented, maintained." />
      </div>
      <div style={{ background: '#0E0F2E', borderRadius: 16, padding: 32, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13, color: '#C4C7DC', overflow: 'auto' }}>
        <div style={{ color: '#8B94A8', marginBottom: 10 }}>{'// Fetch active deals for a merchant'}</div>
        <div><span style={{ color: '#D946EF' }}>const</span> deals = <span style={{ color: '#D946EF' }}>await</span> perkvora.coupons.list({`{`}</div>
        <div style={{ paddingLeft: 16 }}>merchant_id: <span style={{ color: '#F97316' }}>'mch_1a2b3c'</span>,</div>
        <div style={{ paddingLeft: 16 }}>is_active: <span style={{ color: '#F97316' }}>true</span>,</div>
        <div style={{ paddingLeft: 16 }}>limit: <span style={{ color: '#F97316' }}>20</span>,</div>
        <div>{`}`});</div>
      </div>
    </MarketingShell>
  );
}

/* ============= AFFILIATE ============= */
export function AffiliatePage() {
  return (
    <MarketingShell
      eyebrow="🤝 Affiliate Program"
      title="Refer. Earn."
      titleAccent="Repeat."
      lead="Love PerkVora? Get paid to share it. Earn real cashback every time a friend signs up and saves."
      cta={
        <>
          <h2 style={{ fontSize: 'clamp(26px, 3vw, 38px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 14 }}>Start earning today.</h2>
          <p style={{ fontSize: 15, maxWidth: 480, margin: '0 auto 28px', opacity: 0.95 }}>Free to join, no minimums, no limits. Your friends save — you earn.</p>
          <Link to="/register" className="btn btn-xl" style={{ background: '#fff', color: '#1A1B4B', fontWeight: 700 }}>Grab your referral link →</Link>
        </>
      }
    >
      <div className="grid-3" style={{ marginBottom: 56 }}>
        <Perk icon="🔗" t="$10 for you, $10 for them" d="Every referred friend gets $10 off their first cashback. You get $10 once they cash out." />
        <Perk icon="📈" t="Lifetime commissions" d="Earn 5% of every qualifying redemption your referrals make — for the lifetime of their account." />
        <Perk icon="🏆" t="Tier bonuses" d="Refer 5 friends to unlock Gold. Refer 25 to unlock Platinum. Permanent tier upgrades." />
      </div>
      <div style={{ background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 20, padding: 40, textAlign: 'center' }}>
        <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 10 }}>Are you a creator or publisher?</h3>
        <p style={{ fontSize: 15, color: '#4A5568', marginBottom: 20, maxWidth: 520, margin: '0 auto 20px' }}>We offer custom revenue-share deals for influencers, newsletters, and websites. Higher rates, dedicated support, co-branded campaigns.</p>
        <Link to="/contact?topic=partnership" className="btn btn-primary btn-lg">Apply for creator program →</Link>
      </div>
    </MarketingShell>
  );
}
